import { GuidEmpty } from 'mbs-ui-kit/utils/constants';
import { StorageType } from 'mbs-ui-shared@models';
import { PlanTypesDisplay } from 'mbs-ui-shared@models/PlanTypes.enum';
import { BaseForStepsHelper, WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { StepsDataFromPlanHelper } from 'wizards/helpers/steps-data-from-plan-helper/steps-data-from-plan-helper';
import { SupportMethodsForStepsFromPlan } from 'wizards/helpers/support-methods-for-steps-from-plan';
import { BackupFileFormModel } from 'wizards/models/backupFile/backup-form-model';

export class BackupFileModelForStepsHelper extends BaseForStepsHelper {
  public static BackupFileModel: BackupFileFormModel = null;

  public static BackupModelFromPlan(plan, settings: WizardHelpersSettings = this.DefaultSettings): BackupFileFormModel {
    const newPlan = plan && plan.plan;

    if (!newPlan) return this.BackupFileModel;

    this.setThisValues(plan, settings);
    this.setStorageType(settings.mode, newPlan.ConnectionID);

    this.BackupFileModel = new BackupFileFormModel(this.isNBF, this.isUnix && !this.isNetBuild);

    if (this.isCreate) newPlan.AlwaysUseVSS = true;

    if (this.isCreate && settings.agentSimpleUi) {
      this.BackupFileModel.planNameStep = {
        Name: PlanTypesDisplay[this.planType],
        SavePlanInCloud: newPlan.SavePlanInCloud || this.isNBF,
        valid: true
      };
    } else this.updatePlanNameStep(newPlan, settings.compName);

    this.updateWhereBackupStep(newPlan, settings);
    this.BackupFileModel.whatBackupTreeStep = StepsDataFromPlanHelper.updateWhatBackupTree(newPlan);
    this.BackupFileModel.compressionEncryptionStep = StepsDataFromPlanHelper.updateCompressionEncryption(
      newPlan,
      this.isNBF,
      this.isUnix && !this.isNetBuild,
      this.isCreate
    );

    if (!this.isNBF) {
      this.BackupFileModel.retentionPolicyStep = StepsDataFromPlanHelper.updateRetentionPolicy(
        newPlan,
        this.isCreate,
        this.planType,
        this.isUnix && !this.isNetBuild
      );
      this.BackupFileModel.scheduleStep = StepsDataFromPlanHelper.updateSchedule(
        newPlan,
        plan.OverdueAfterSuccess,
        this.isCreate,
        this.isUnix && !this.isNetBuild
      );
    } else {
      this.bucketId = this.accounts && this.isRD ? this.getBucketId(newPlan.Bucket, newPlan.SGAccountID) : newPlan.ConnectionID;
      this.BackupFileModel.retentionPolicyStep = StepsDataFromPlanHelper.updateNBFRetentionPolicy(
        newPlan,
        SupportMethodsForStepsFromPlan.needShowImmutability(this.storages.CloudStorages, this.bucketId),
        this.isCreate,
        this.isUnix && !this.isNetBuild,
        this.isNBF
      );

      if (this.nbfCanHybrid && !!newPlan.HybridConnections?.length) {
        this.BackupFileModel.retentionPolicyStep = StepsDataFromPlanHelper.updateNBFHybridRetentionPolicy(
          newPlan,
          this.BackupFileModel.retentionPolicyStep
        );
      }

      this.BackupFileModel.archiveConsistencyStep = StepsDataFromPlanHelper.updateArchiveConsistency(newPlan);
      this.BackupFileModel.scheduleStep = StepsDataFromPlanHelper.updateNBFSchedule(
        newPlan,
        plan.OverdueAfterSuccess,
        this.isCreate,
        this.isUnix && !this.isNetBuild,
        settings.isNBF,
        settings.isRD || settings.validVersionForSchedule
      );
    }

    this.updateAdvancedOptionsData(newPlan, settings);
    this.BackupFileModel.advancedFilterStep = StepsDataFromPlanHelper.updateAdvancedFilter(newPlan, this.isUnix && !this.isNetBuild);
    this.BackupFileModel.prePostActionsStep = StepsDataFromPlanHelper.updatePrePostActions(newPlan, false, this.isUnix && !this.isNetBuild);

    if (!this.isUnix || this.isNetBuild) {
      this.BackupFileModel.AvailableMachinePlans = StepsDataFromPlanHelper.updateAvailableMachinePlans(
        plan.AvailableMachinePlans,
        newPlan.ID
      );
    }

    this.BackupFileModel.notificationStep = StepsDataFromPlanHelper.updateNotification(newPlan.Notification);

    return this.BackupFileModel;
  }

  public static updatePlanNameStep(newPlan, compName: string): void {
    const name = newPlan.Name || SupportMethodsForStepsFromPlan.generatePlanName(compName, this.planType, this.timeZoneOffset);
    this.BackupFileModel.planNameStep = StepsDataFromPlanHelper.updatePlanNameStep(name, newPlan.SavePlanInCloud, this.isNBF);
  }

  public static updateWhereBackupStep(newPlan, settings: WizardHelpersSettings): void {
    if (!this.accounts) {
      this.updateWhereBackupStepData(newPlan, this.BackupFileModel, settings);

      return this.updateStorages();
    }

    const id = this.bucketId || this.getBucketId(newPlan.Bucket, newPlan.SGAccountID);

    if (id && id !== newPlan.ConnectionID && !this.storage) this.setStorageType(settings.mode, id);

    this.updateWhereBackupStepData(newPlan, this.BackupFileModel, settings, id);

    if (id && id !== GuidEmpty) {
      const storage = this.storages.CloudStorages.find((s) => s.ID === id) || this.storages.LocalStorages.find((s) => s.ID === id);
      this.storageType = storage ? storage.StorageType : this.storageType;
    }

    this.updateStorages();
  }

  public static updateAdvancedOptionsData(newPlan, settings?: WizardHelpersSettings): void {
    const advancedOptionsData = StepsDataFromPlanHelper.updateAdvancedOptionsForBackup(
      newPlan,
      this.storage,
      this.isUnix,
      this.isNBF,
      settings.agentVersion,
      this.isNetBuild
    );
    this.updateEFSData(newPlan, settings.isNBF, advancedOptionsData);

    if (this.isCreate && this.isNBF && !advancedOptionsData.advancedOptionsStep.SyntheticFull) {
      advancedOptionsData.advancedOptionsStep.SyntheticFull = true;
    }

    this.BackupFileModel.advancedOptionsStep = advancedOptionsData.advancedOptionsStep;
    this.BackupFileModel.advancedOptionsFlags = advancedOptionsData.advancedOptionsFlags;
  }

  private static updateStorages(): void {
    if (!this.isRD && this.isNBF) SupportMethodsForStepsFromPlan.getFilteredStoragesByTypes(this.storages, [StorageType.OpenStack]);

    this.BackupFileModel.Storages = this.storages;
  }
}
